import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { Trans, useLingui } from "@lingui/macro";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@app/components/ui/card";
import { Badge } from "@app/components/ui/badge";
import { Button } from "@app/components/ui/button";
import {
  ChevronDown,
  ChevronRight,
  EyeOff,
  Loader2,
  PencilLine,
} from "lucide-react";
import { cn } from "@app/lib/utils";
import API from "@app/services/api";
import type { CorrectionErrorClass } from "@app/types/correction";
import {
  IN_RECORD_ORDER,
  NOT_IN_RECORD_ORDER,
  errorClassOption,
} from "@app/utils/correction-classes";

/**
 * What the doctors corrected, in what proportion, and why.
 *
 * Reads `GET /correction/stats`. Every number on screen is computed by
 * `services/correction_stats.py`, never here: two implementations of "was this value
 * changed" is how a metric stops being comparable between this screen and the per-field
 * reading of the same log beside each ontology field.
 *
 * The panel is deliberately built around a distinction the backend enforces and the
 * interface must not blur — a **gesture** (the doctor corrected, validated or flagged) is
 * recorded on every row ever written, while an **error class** only exists on corrections
 * saved since the class was introduced. Merging the two would produce a chart whose
 * denominator nobody could name.
 */

/**
 * A per-record statistic, written as "median (mean)".
 *
 * Both numbers or neither: review effort is long-tailed, so a mean alone describes a
 * typical record nobody has, and a median alone hides the dossier carrying forty
 * corrections.
 */
const fmtStat = (median: number | null, mean: number | null): string => {
  if (median === null || mean === null) return "—";
  return `${median.toFixed(median % 1 === 0 ? 0 : 1)} (moy. ${mean.toFixed(1)})`;
};

/** The three gestures, in the fixed order they are always drawn in. */
type Gesture = "validated" | "corrected" | "flagged";

/** How the value moved, for the corrections that moved one. */
type Shape = "filled" | "emptied" | "replaced";

interface FieldStats {
  field_key: string;
  name: string | null;
  group_name: string | null;
  /** Où ce champ se MODIFIE : sa ligne d'ontologie et le stack qui la porte. Les deux nuls
   *  pour un geste posé sur un champ que l'ontologie ne déclare plus — le classement le
   *  nomme encore, il n'a simplement nulle part où envoyer le lecteur. */
  field_id: string | null;
  ontology_stack_id: string | null;
  reviews: number;
  validated: number;
  corrected: number;
  flagged: number;
  disagreements: number;
  disagreement_rate: number | null;
  non_scorable: number;
  patients_reviewed: number;
  /** Null when the scope pins no set of records — see `patients_in_scope`. */
  patients_unreviewed: number | null;
  expected_remaining: number | null;
  shapes: Record<Shape, number>;
  classes: Record<CorrectionErrorClass, number>;
  unclassified: number;
  misfiled_to: Record<string, number>;
  justification_sources: Record<string, number>;
  last_at: string | null;
}

/** An ontology field of this scope's speciality that nobody has reviewed once. */
interface NeverReviewedField {
  field_key: string;
  name: string | null;
  group_name: string | null;
}

/** The prose one of the five irreducible classes required.
 *
 * `cited_a_passage` is still computed by the backend — a legacy client could post a
 * verbatim and the column outlives the picker — but nothing renders it any more: the
 * passage picker produced zero citations across 169 classified corrections and was
 * removed, so a badge for it could only ever be off. */
interface Justification {
  field_key: string;
  field_name: string | null;
  error_class: CorrectionErrorClass;
  note: string;
  target_field: string | null;
  at: string | null;
}

/** One reviewed record: what its fields say today, not how much work went into them. */
interface PatientStats {
  tenant_id: string;
  id_patient: number;
  fields_reviewed: number;
  fields_validated: number;
  fields_corrected: number;
  fields_flagged: number;
  last_at: string | null;
}

interface CorrectionStats {
  scope: {
    specialite: string | null;
    dataset_id: string | null;
    since: string | null;
    until: string | null;
  };
  totals: {
    reviews: number;
    validated: number;
    corrected: number;
    flagged: number;
    disagreements: number;
    disagreement_rate: number | null;
    fields: number;
    patients: number;
    /** How many records the scope pins. Null for "everything I can see", which pins none. */
    patients_in_scope: number | null;
    authors: number;
    non_scorable: number;
    /** Records carrying at least one gesture. */
    patients_reviewed: number;
    /** Averaged over the REVIEWED records only — never over the scope. */
    mean_fields_reviewed: number | null;
    median_fields_reviewed: number | null;
    mean_fields_corrected: number | null;
    median_fields_corrected: number | null;
    /** Gestures on fields the ontology no longer declares. */
    retired_reviews: number;
  };
  shapes: Record<Shape, number>;
  classes: Record<CorrectionErrorClass, number>;
  justification_sources: Record<string, number>;
  classification: {
    classified: number;
    unclassified_before_classes: number;
    unclassified_since_classes: number;
    first_classified_at: string | null;
  };
  /** Each misfiling as a pair — the unit of meaning is the boundary, not one side. */
  misfiled: { from_field: string; to_field: string; count: number }[];
  justifications: Justification[];
  /** How many the scope holds; `justifications` carries at most the newest 50. */
  justifications_total: number;
  never_reviewed: NeverReviewedField[];
  retired_fields: { field_key: string; reviews: number }[];
  by_patient: PatientStats[];
  by_field: FieldStats[];
}

interface ApiEnvelope<T> {
  ok: boolean;
  data?: T;
  error?: string;
}

/**
 * The gesture colours, validated with the palette checker: teal / primary / purple pass
 * the lightness band, the chroma floor, CVD separation (worst adjacent ΔE 8.4 protan) and
 * contrast against the surface. All three are cool, as the project's palette requires.
 *
 * They are only ever used on the proportion bars — every figure beside them is written in
 * slate ink, so identity never rests on colour alone.
 */
const GESTURE_COLOR: Record<Gesture, string> = {
  validated: "#0d9488",
  corrected: "#1752B7",
  flagged: "#9333ea",
};

const GESTURE_ORDER: Gesture[] = ["validated", "corrected", "flagged"];

const SHAPE_ORDER: Shape[] = ["filled", "replaced", "emptied"];

/**
 * How many reviewed records the table draws at once, and how many each scroll adds.
 *
 * The scope carries hundreds of records and the payload already holds them all — what is
 * paged here is the DOM, not the fetch: drawing two hundred rows nobody scrolls to costs
 * a layout pass on every re-render of the panel.
 */
const PATIENT_PAGE_SIZE = 10;

/** A fraction as a percentage, or an em dash when there is nothing to divide. */
function percent(value: number | null | undefined, total?: number): string {
  const ratio =
    total !== undefined ? (total > 0 ? (value ?? 0) / total : null) : value;
  if (ratio === null || ratio === undefined) return "—";
  return `${(ratio * 100).toFixed(ratio >= 0.1 ? 0 : 1)} %`;
}

function formatMonth(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

/**
 * One bar, three segments, always summing to the row's reviews.
 *
 * Segments are separated by a 2px surface gap so adjacent fills never touch, and a segment
 * that would render thinner than a hairline is dropped rather than drawn as a sliver that
 * reads like a rounding artefact.
 */
function GestureBar({
  stats,
  height = 8,
}: {
  stats: Pick<FieldStats, "reviews" | "validated" | "corrected" | "flagged">;
  height?: number;
}) {
  const total = stats.reviews || 1;
  return (
    <div
      className="flex w-full overflow-hidden rounded-full bg-slate-100"
      style={{ height, gap: 2 }}
    >
      {GESTURE_ORDER.map((gesture) => {
        const value = stats[gesture];
        if (!value) return null;
        return (
          <div
            key={gesture}
            style={{
              width: `${(value / total) * 100}%`,
              backgroundColor: GESTURE_COLOR[gesture],
            }}
            className="first:rounded-l-full last:rounded-r-full"
          />
        );
      })}
    </div>
  );
}

/** A ranked single-hue bar: one measure across labelled categories, identity in the text. */
function RankedBar({
  label,
  hint,
  value,
  total,
}: {
  label: string;
  hint?: string;
  value: number;
  total: number;
}) {
  const ratio = total > 0 ? value / total : 0;
  return (
    <div className="py-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm text-slate-700">{label}</span>
        <span className="shrink-0 font-mono text-xs text-slate-500">
          {value} · {percent(ratio)}
        </span>
      </div>
      <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-slate-100">
        <div
          className="h-full rounded-full bg-primary"
          style={{ width: `${Math.max(ratio * 100, value > 0 ? 1.5 : 0)}%` }}
        />
      </div>
      {hint && <p className="mt-1 text-3xs text-slate-500">{hint}</p>}
    </div>
  );
}

interface CorrectionsPanelProps {
  /**
   * Restrict to the patients a dataset pins. Optional and not the default scope: most
   * corrections are written on records that belong to no dataset, so a dataset-scoped
   * reading shows a small fraction of the log.
   */
  datasetId?: string;
  /** Rendered inside a tab rather than as a page — drops the panel's own heading. */
  embedded?: boolean;
}

export function CorrectionsPanel({
  datasetId,
  embedded = false,
}: CorrectionsPanelProps) {
  const { t } = useLingui();
  const [stats, setStats] = useState<CorrectionStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [showAllFields, setShowAllFields] = useState(false);
  /** How many rows of `by_patient` are currently drawn — grows as the reader scrolls. */
  const [patientLimit, setPatientLimit] = useState(PATIENT_PAGE_SIZE);
  // "Which field is worst" and "where should I look next" are different questions and the
  // second one only exists when the scope pins a set of records to still look at.
  const [sortBy, setSortBy] = useState<"rate" | "yield">("yield");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = (await API.get({
        path: "/correction/stats",
        query: {
          with_justifications: true,
          ...(datasetId ? { dataset_id: datasetId } : {}),
        },
      })) as ApiEnvelope<CorrectionStats>;
      if (resp.ok && resp.data) {
        setStats(resp.data);
        setPatientLimit(PATIENT_PAGE_SIZE);
      } else {
        setError(resp.error || t`Impossible de charger les corrections.`);
      }
    } catch {
      setError(t`Impossible de charger les corrections.`);
    } finally {
      setLoading(false);
    }
    // `t` is stable for a locale; re-creating the callback on every render would refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datasetId]);

  useEffect(() => {
    void load();
  }, [load]);

  const gestureLabel: Record<Gesture, string> = useMemo(
    () => ({
      validated: t`Validées`,
      corrected: t`Corrigées`,
      flagged: t`Signalées sans correction`,
    }),
    [t],
  );

  // How the class was arrived at. A class the doctor never looked at is weaker evidence than
  // one they arbitrated, and whoever acts on this panel has to weigh the two apart — so the
  // screen reports which of the four happened rather than flattening all to "classified".
  const sourceLabel: Record<string, string> = useMemo(
    () => ({
      manual: t`choisie par le médecin`,
      overridden: t`proposition corrigée par le médecin`,
      confirmed: t`proposition confirmée`,
      auto: t`pré-remplie, jamais arbitrée`,
    }),
    [t],
  );

  /** Near the bottom of the record list, reveal one more page. */
  const handlePatientScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const el = event.currentTarget;
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 48) {
        setPatientLimit((limit) => limit + PATIENT_PAGE_SIZE);
      }
    },
    [],
  );

  const shapeLabel: Record<Shape, string> = useMemo(
    () => ({
      filled: t`Champ vide, valeur ajoutée`,
      replaced: t`Valeur remplacée par une autre`,
      emptied: t`Valeur retirée, champ vidé`,
    }),
    [t],
  );

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-6 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        <Trans>Chargement des corrections…</Trans>
      </div>
    );
  }

  if (error) {
    return (
      <p className="p-6 text-sm text-red-700" role="alert">
        {error}
      </p>
    );
  }

  if (!stats) return null;

  const { totals, classification } = stats;

  if (totals.reviews === 0) {
    return (
      <Card>
        <CardContent className="py-10 text-center">
          <PencilLine className="mx-auto h-8 w-8 text-slate-300" />
          <p className="mt-3 text-sm font-medium text-slate-700">
            <Trans>Aucune relecture dans ce périmètre</Trans>
          </p>
          <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">
            {datasetId ? (
              <Trans>
                Aucun patient de ce jeu de données n'a encore été relu au crayon.
                La très grande majorité des relectures portent sur des dossiers
                qui n'appartiennent à aucun jeu de données : la vue complète est
                dans Laboratoire → Corrections.
              </Trans>
            ) : (
              <Trans>
                Les relectures apparaîtront ici dès qu'un dossier sera corrigé ou
                validé depuis la fiche patient.
              </Trans>
            )}
          </p>
        </CardContent>
      </Card>
    );
  }

  // The backend ranks by rate. Re-ranking by expected yield is a client-side reorder of
  // the same rows — the numbers themselves are never recomputed here.
  const hasDenominator = totals.patients_in_scope !== null;
  const orderedFields =
    hasDenominator && sortBy === "yield"
      ? [...stats.by_field].sort(
          (a, b) => (b.expected_remaining ?? 0) - (a.expected_remaining ?? 0),
        )
      : stats.by_field;
  const rankedFields = showAllFields ? orderedFields : orderedFields.slice(0, 15);
  // Target fields arrive as keys. They are named from the rows already on screen, falling
  // back to the key — a field that has never been corrected has no row to borrow a name
  // from, and showing its key beats showing nothing.
  const nameByKey = new Map<string, string>([
    ...stats.by_field.map(
      (field) => [field.field_key, field.name || field.field_key] as const,
    ),
    ...stats.never_reviewed.map(
      (field) => [field.field_key, field.name || field.field_key] as const,
    ),
  ]);
  const fieldName = (key: string) => nameByKey.get(key) ?? key;
  const totalCorrectionsWithShape = SHAPE_ORDER.reduce(
    (sum, shape) => sum + stats.shapes[shape],
    0,
  );
  const totalDisagreements = totals.disagreements;

  return (
    <div className="space-y-6">
      {!embedded && (
        <div>
          <h2 className="text-xl font-semibold text-slate-900">
            <Trans>Ce que les médecins corrigent</Trans>
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            <Trans>
              Chaque passage du crayon sur une fiche patient est enregistré. Cette
              page lit ce journal dans l'autre sens : non pas « qu'a-t-on corrigé
              sur ce dossier », mais « quels champs le modèle rate, et pourquoi ».
            </Trans>
          </p>
        </div>
      )}

      {/* What these numbers cover, said out loud. The scope is the visibility filter every
          patient screen uses — the caller's speciality for a reviewer or an admin, their
          own patients for a doctor — and a reader who has to infer that from the totals
          will infer it wrong. */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-slate-500">
        <span className="font-medium text-slate-600">
          <Trans>Périmètre :</Trans>
        </span>
        {stats.scope.specialite ? (
          <Badge variant="outline" className="font-normal">
            <Trans>spécialité {stats.scope.specialite}</Trans>
          </Badge>
        ) : (
          <Badge variant="outline" className="font-normal">
            <Trans>toutes spécialités</Trans>
          </Badge>
        )}
        <span>·</span>
        <span>
          {stats.scope.dataset_id ? (
            <Trans>
              les {totals.patients_in_scope} dossiers de ce jeu de données
            </Trans>
          ) : (
            <Trans>
              tous les dossiers que vous pouvez ouvrir, jeu de données ou non
            </Trans>
          )}
        </span>
      </div>

      {/* ═══ Combien de dossiers ont été revus, et jusqu'où ═══ */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            <Trans>Couverture de la revue</Trans>
          </CardTitle>
          <p className="text-sm text-slate-500">
            {totals.patients_in_scope !== null ? (
              <Trans>
                Combien des dossiers épinglés quelqu'un a ouverts, et combien de champs
                il y a statués. Un dossier absent de cette liste n'a reçu aucun geste.
              </Trans>
            ) : (
              <Trans>
                Combien de dossiers ont reçu au moins un geste, et combien de champs y
                ont été statués.
              </Trans>
            )}
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 @sm:grid-cols-3">
            <div className="rounded-lg bg-blue-50 px-3 py-2">
              <div className="text-xs text-slate-600">
                <Trans>Dossiers revus</Trans>
              </div>
              <div className="text-lg font-semibold text-blue-900">
                {totals.patients_in_scope !== null
                  ? `${totals.patients_reviewed} / ${totals.patients_in_scope}`
                  : totals.patients_reviewed}
              </div>
            </div>
            <div className="rounded-lg bg-blue-50 px-3 py-2">
              <div className="text-xs text-slate-600">
                <Trans>Champs revus par dossier</Trans>
              </div>
              <div className="text-lg font-semibold text-blue-900">
                {fmtStat(totals.median_fields_reviewed, totals.mean_fields_reviewed)}
              </div>
            </div>
            <div className="rounded-lg bg-blue-50 px-3 py-2">
              <div className="text-xs text-slate-600">
                <Trans>Champs corrigés par dossier</Trans>
              </div>
              <div className="text-lg font-semibold text-blue-900">
                {fmtStat(totals.median_fields_corrected, totals.mean_fields_corrected)}
              </div>
            </div>
          </div>

          {/* Médiane ET moyenne : l'effort de relecture est à queue longue — un dossier
              peut porter quarante corrections quand la plupart en portent deux — et la
              moyenne seule décrirait un dossier type qui n'existe pas. Les deux sont
              calculées sur les dossiers REVUS, jamais sur le périmètre : diviser par le
              périmètre confondrait « rien à corriger » et « jamais ouvert ». */}
          {stats.by_patient.length > 0 && (
            <div
              className="max-h-96 overflow-auto"
              onScroll={handlePatientScroll}
            >
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-10 bg-white">
                  <tr className="border-b border-slate-200 text-left text-xs text-slate-500">
                    <th className="bg-white py-2 pr-3 font-medium">
                      <Trans>Dossier</Trans>
                    </th>
                    <th className="bg-white py-2 pr-3 text-right font-medium">
                      <Trans>Champs revus</Trans>
                    </th>
                    <th className="bg-white py-2 pr-3 text-right font-medium">
                      <Trans>Validés</Trans>
                    </th>
                    <th className="bg-white py-2 pr-3 text-right font-medium">
                      <Trans>Corrigés</Trans>
                    </th>
                    <th className="bg-white py-2 text-right font-medium">
                      <Trans>Sans changement</Trans>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {stats.by_patient.slice(0, patientLimit).map((row) => (
                    <tr
                      key={`${row.tenant_id}-${row.id_patient}`}
                      className="border-b border-slate-100 last:border-0"
                    >
                      <td className="py-2 pr-3">
                        <Link
                          to={`/patient/${row.tenant_id}/${row.id_patient}`}
                          className="text-primary hover:underline"
                        >
                          {row.id_patient}
                        </Link>
                      </td>
                      <td className="py-2 pr-3 text-right font-medium text-slate-900">
                        {row.fields_reviewed}
                      </td>
                      <td className="py-2 pr-3 text-right text-slate-600">
                        {row.fields_validated}
                      </td>
                      <td className="py-2 pr-3 text-right text-slate-600">
                        {row.fields_corrected}
                      </td>
                      <td className="py-2 text-right text-slate-600">
                        {row.fields_flagged}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* Le reste se révèle au défilement — le bouton reste là pour qui ne
                  défile pas avec une souris (clavier, lecteur d'écran). */}
              {stats.by_patient.length > PATIENT_PAGE_SIZE && (
                <div className="flex flex-wrap items-center gap-3 pt-2">
                  {stats.by_patient.length > patientLimit && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setPatientLimit((limit) => limit + PATIENT_PAGE_SIZE)
                      }
                    >
                      <Trans>Afficher plus de dossiers</Trans>
                    </Button>
                  )}
                  <span className="text-xs text-slate-500">
                    <Trans>
                      {Math.min(patientLimit, stats.by_patient.length)} dossiers
                      affichés sur {stats.by_patient.length}, les plus corrigés
                      d'abord.
                    </Trans>
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Des gestes portés sur des champs que l'ontologie ne déclare plus. Comptés à
              part et jamais dans les totaux : ils ne sont jamais arrivés jusqu'à la ligne
              de vérité (le write path exige la colonne), donc les créditer ferait état
              d'une relecture de quelque chose que personne ne peut ouvrir. */}
          {totals.retired_reviews > 0 && (
            <p className="text-xs text-slate-500">
              <Trans>
                {totals.retired_reviews} gestes portent sur des champs retirés de
                l'ontologie ({stats.retired_fields.map((f) => f.field_key).join(", ")})
                et ne sont comptés nulle part ci-dessus.
              </Trans>
            </p>
          )}
        </CardContent>
      </Card>

      {/* ═══ La proportion, d'abord ═══ */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            <Trans>Proportion des relectures</Trans>
          </CardTitle>
          <p className="mt-1 text-sm text-slate-500">
            <Trans>
              Sur {totals.reviews} relectures portant sur {totals.patients}{" "}
              dossiers et {totals.fields} champs.
            </Trans>
          </p>
        </CardHeader>
        <CardContent>
          <div className="mb-5 flex flex-wrap items-end gap-x-10 gap-y-4">
            <div>
              <div className="text-3xl font-semibold tabular-nums text-slate-900">
                {percent(totals.disagreement_rate)}
              </div>
              <div className="text-sm text-slate-500">
                <Trans>des relectures ne valident pas l'extraction</Trans>
              </div>
            </div>
            {GESTURE_ORDER.map((gesture) => (
              <div key={gesture}>
                <div className="flex items-center gap-2">
                  <span
                    className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
                    style={{ backgroundColor: GESTURE_COLOR[gesture] }}
                    aria-hidden="true"
                  />
                  <span className="text-xl font-semibold tabular-nums text-slate-900">
                    {totals[gesture]}
                  </span>
                  <span className="text-sm text-slate-500">
                    {percent(totals[gesture], totals.reviews)}
                  </span>
                </div>
                <div className="mt-0.5 text-sm text-slate-500">
                  {gestureLabel[gesture]}
                </div>
              </div>
            ))}
          </div>

          <GestureBar stats={totals} height={12} />

          <p className="mt-3 text-xs text-slate-500">
            <Trans>
              « Signalées sans correction » : le médecin a marqué la valeur comme
              fausse sans en saisir une autre. C'est une correction inachevée — le
              champ est faux, mais le journal ne porte pas la bonne réponse.
            </Trans>
          </p>
          {totals.non_scorable > 0 && (
            <p className="mt-2 text-xs text-slate-500">
              <Trans>
                Dont {totals.non_scorable} classées « le document se trompe ou
                n'en dit rien » : aucune lecture des documents n'aurait pu
                produire la bonne valeur. Elles mesurent le corpus, pas le
                modèle, et ne devraient pas peser dans un score d'extraction.
              </Trans>
            </p>
          )}
        </CardContent>
      </Card>

      {/* ═══ Quels champs ═══ */}
      <Card data-tour="corrections-fields">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            <Trans>Quels champs sont repris</Trans>
          </CardTitle>
          {hasDenominator && (
            <div className="mt-2 flex gap-1">
              <Button
                size="sm"
                variant={sortBy === "yield" ? "default" : "outline"}
                onClick={() => setSortBy("yield")}
              >
                <Trans>Où porter l'effort</Trans>
              </Button>
              <Button
                size="sm"
                variant={sortBy === "rate" ? "default" : "outline"}
                onClick={() => setSortBy("rate")}
              >
                <Trans>Taux de désaccord</Trans>
              </Button>
            </div>
          )}
          <p className="mt-2 text-sm text-slate-500">
            {hasDenominator && sortBy === "yield" ? (
              <Trans>
                Classés par gisement estimé : le taux de désaccord multiplié par les
                dossiers du jeu de données que personne n'a encore relus sur ce
                champ. Un taux seul ne dit pas où chercher — un champ faux 4 fois sur
                6 cache bien plus qu'un champ faux 150 fois sur 230 déjà épuisé.
              </Trans>
            ) : (
              <Trans>
                Classés par taux de désaccord, pas par volume : un champ repris 12
                fois sur 15 est un signal plus fort qu'un champ repris 40 fois sur
                400.
              </Trans>
            )}
          </p>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="divide-y divide-slate-100">
            {rankedFields.map((field, index) => {
              const isOpen = expanded === field.field_key;
              const classEntries = (
                Object.entries(field.classes) as [CorrectionErrorClass, number][]
              ).filter(([, count]) => count > 0);
              return (
                <div
                  key={field.field_key}
                  data-tour={index === 0 ? "corrections-top-field" : undefined}
                >
                  <button
                    type="button"
                    onClick={() =>
                      setExpanded(isOpen ? null : field.field_key)
                    }
                    className="flex w-full items-center gap-3 py-2.5 text-left hover:bg-slate-50"
                    aria-expanded={isOpen}
                  >
                    {isOpen ? (
                      <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
                    ) : (
                      <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2">
                        <span className="truncate text-sm font-medium text-slate-900">
                          {field.name || field.field_key}
                        </span>
                        {field.group_name && (
                          <span className="hidden truncate text-xs text-slate-400 sm:inline">
                            {field.group_name}
                          </span>
                        )}
                      </div>
                      <div className="mt-1.5 max-w-md">
                        <GestureBar stats={field} />
                      </div>
                      {hasDenominator && (
                        <div className="mt-1 flex flex-wrap gap-x-3 text-3xs text-slate-500">
                          <span>
                            <Trans>
                              {field.patients_reviewed}/
                              {totals.patients_in_scope} dossiers relus
                            </Trans>
                          </span>
                          {/* A rate off a handful of reviews is not a rate. Saying so
                              beside it is cheaper than a doctor discovering it after
                              spending an afternoon on the field it put on top. */}
                          {field.reviews < 10 && (
                            <span className="text-slate-400">
                              <Trans>taux sur {field.reviews} relectures seulement</Trans>
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="shrink-0 text-right">
                      {hasDenominator && sortBy === "yield" ? (
                        <>
                          <div className="font-mono text-sm tabular-nums text-slate-900">
                            ≈ {Math.round(field.expected_remaining ?? 0)}
                          </div>
                          <div className="text-3xs text-slate-500">
                            <Trans>
                              à trouver · {percent(field.disagreement_rate)}
                            </Trans>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="font-mono text-sm tabular-nums text-slate-900">
                            {percent(field.disagreement_rate)}
                          </div>
                          <div className="text-3xs text-slate-500">
                            {field.disagreements}/{field.reviews}
                          </div>
                        </>
                      )}
                    </div>
                  </button>

                  {isOpen && (
                    <div className="grid gap-6 border-l-2 border-slate-100 pb-4 pl-7 pt-1 sm:grid-cols-2">
                      <div>
                        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                          <Trans>Comment la valeur a changé</Trans>
                        </div>
                        <div className="mt-1">
                          {SHAPE_ORDER.map((shape) => (
                            <RankedBar
                              key={shape}
                              label={shapeLabel[shape]}
                              value={field.shapes[shape]}
                              total={field.disagreements}
                            />
                          ))}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                          <Trans>Pourquoi</Trans>
                        </div>
                        {classEntries.length === 0 ? (
                          <p className="mt-2 text-sm text-slate-500">
                            <Trans>
                              Aucune des {field.disagreements} reprises de ce
                              champ ne porte de classe d'erreur.
                            </Trans>
                          </p>
                        ) : (
                          <div className="mt-1">
                            {classEntries
                              .sort((a, b) => b[1] - a[1])
                              .map(([klass, count]) => (
                                <RankedBar
                                  key={klass}
                                  label={t(errorClassOption(klass).label)}
                                  value={count}
                                  total={field.disagreements}
                                />
                              ))}
                            {field.unclassified > 0 && (
                              <p className="mt-2 text-xs text-slate-500">
                                <Trans>
                                  + {field.unclassified} sans classe.
                                </Trans>
                              </p>
                            )}
                          </div>
                        )}
                        {/* The repair, not just the diagnosis: "ça aurait dû aller
                            ailleurs" is only actionable once the destination is named,
                            because the defect is the boundary between the two field
                            descriptions. */}
                        {Object.keys(field.misfiled_to).length > 0 && (
                          <div className="mt-3">
                            <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                              <Trans>Aurait dû aller dans</Trans>
                            </div>
                            <ul className="mt-1 space-y-0.5">
                              {Object.entries(field.misfiled_to).map(
                                ([target, count]) => (
                                  <li
                                    key={target}
                                    className="text-sm text-slate-700"
                                  >
                                    {fieldName(target)}{" "}
                                    <span className="font-mono text-xs text-slate-500">
                                      ×{count}
                                    </span>
                                  </li>
                                ),
                              )}
                            </ul>
                          </div>
                        )}
                        {/* Le pas suivant, et il manquait.
                            Cet écran dit QUEL champ reprendre ; il ne dit rien de ce qu'il
                            faut y changer, et il ne montre jamais une valeur — ni
                            ``old_value``, ni ``new_value``, ni le passage cité, dont il ne
                            publie qu'un booléen. Tout cela vit sur la définition du champ,
                            derrière « Retours cliniciens ». Sans ce lien il fallait y
                            retourner à pied par Laboratoire → Ontologie → Gérer les champs,
                            et un écran qu'on rejoint à pied finit par ne plus être ouvert :
                            c'est la cause exacte des 830 propositions restées sans lecteur.
                            Absent quand l'ontologie ne déclare plus ce champ — le lien
                            n'aurait pas de cible. */}
                        {field.ontology_stack_id && field.field_id && (
                          <Link
                            to={`/laboratoire/ontologie?stack=${encodeURIComponent(field.ontology_stack_id)}&field=${encodeURIComponent(field.field_id)}`}
                            className="mt-4 inline-flex items-center gap-1.5 rounded-md border border-primary/30 px-2.5 py-1.5 text-xs font-medium text-primary hover:border-primary/50 hover:bg-primary/5"
                          >
                            <PencilLine className="h-3.5 w-3.5" aria-hidden />
                            <Trans>Ouvrir la définition et les retours de ce champ</Trans>
                          </Link>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {stats.by_field.length > 15 && (
            <Button
              variant="ghost"
              size="sm"
              className="mt-3"
              onClick={() => setShowAllFields((value) => !value)}
            >
              {showAllFields ? (
                <Trans>Afficher les 15 premiers</Trans>
              ) : (
                <Trans>Afficher les {stats.by_field.length} champs</Trans>
              )}
            </Button>
          )}

          {/* The blind spot. These fields have no row above because nobody reviewed them
              once, and an absent row reads as "nothing to do here" — the exact opposite of
              what it means. Their rate is not low, it is unknown. */}
          {stats.never_reviewed.length > 0 && (
            <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-center gap-2">
                <EyeOff className="h-4 w-4 shrink-0 text-slate-500" />
                <span className="text-sm font-medium text-slate-900">
                  <Trans>
                    {stats.never_reviewed.length} champs jamais relus dans ce
                    périmètre
                  </Trans>
                </span>
              </div>
              <p className="mt-1 text-xs text-slate-600">
                <Trans>
                  Ils n'ont pas de taux : personne ne les a regardés une seule fois.
                  Leur absence du classement ci-dessus ne veut pas dire qu'ils sont
                  justes, elle veut dire qu'on ne sait pas.
                </Trans>
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {stats.never_reviewed.map((field) => (
                  <Badge
                    key={field.field_key}
                    variant="outline"
                    className="bg-white font-normal"
                  >
                    {field.name || field.field_key}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ═══ Pourquoi ═══ */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-base">
              <Trans>Pourquoi l'extraction était fausse</Trans>
            </CardTitle>
            <Badge variant="outline" className="font-normal">
              <Trans>
                {classification.classified} corrections justifiées sur{" "}
                {totalDisagreements}
              </Trans>
            </Badge>
          </div>
          <p className="mt-1 text-sm text-slate-500">
            {classification.first_classified_at ? (
              <Trans>
                Les classes d'erreur sont demandées depuis le{" "}
                {formatMonth(classification.first_classified_at)}.
              </Trans>
            ) : (
              <Trans>
                Aucune correction ne porte encore de classe d'erreur : la question
                n'est posée au médecin que depuis la refonte du crayon.
              </Trans>
            )}
          </p>
        </CardHeader>
        <CardContent>
          {classification.classified === 0 ? (
            <p className="max-w-2xl text-sm text-slate-600">
              <Trans>
                Les {classification.unclassified_before_classes} corrections
                antérieures ne peuvent pas être rattrapées : elles n'ont jamais
                porté de raison, et la deviner reviendrait à écrire une hypothèse
                de machine dans la colonne où vit le verdict d'un médecin. Cette
                section se remplira au fil des prochaines corrections.
              </Trans>
            </p>
          ) : (
            <>
              <div className="grid gap-x-10 sm:grid-cols-2">
                <div>
                  <div className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">
                    <Trans>La valeur était dans le dossier</Trans>
                  </div>
                  {IN_RECORD_ORDER.map((klass) => (
                    <RankedBar
                      key={klass}
                      label={t(errorClassOption(klass).label)}
                      hint={t(errorClassOption(klass).hint)}
                      value={stats.classes[klass]}
                      total={classification.classified}
                    />
                  ))}
                </div>
                <div>
                  <div className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">
                    <Trans>La valeur n'était pas dans le dossier</Trans>
                  </div>
                  {NOT_IN_RECORD_ORDER.map((klass) => (
                    <RankedBar
                      key={klass}
                      label={t(errorClassOption(klass).label)}
                      hint={t(errorClassOption(klass).hint)}
                      value={stats.classes[klass]}
                      total={classification.classified}
                    />
                  ))}
                </div>
              </div>
              {/* Provenance, because a class is evidence and evidence has a weight.
                  "auto" means the machine proposed it and nobody arbitrated: that is a
                  pre-fill's opinion wearing a clinician's authority, and it must never be
                  read as a doctor's verdict. */}
              <div className="mt-5 border-t border-slate-100 pt-4">
                <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                  <Trans>Comment la classe a été établie</Trans>
                </div>
                <div className="mt-2 flex flex-wrap gap-x-6 gap-y-2">
                  {Object.entries(stats.justification_sources)
                    .filter(([, count]) => count > 0)
                    .map(([source, count]) => (
                      <div key={source} className="text-sm">
                        <span className="font-semibold tabular-nums text-slate-900">
                          {count}
                        </span>{" "}
                        <span className="text-slate-500">
                          {sourceLabel[source] ?? source}
                        </span>
                      </div>
                    ))}
                </div>
              </div>

              {/* Every misfiling as a pair. One line here IS an ontology defect: two field
                  descriptions that do not divide the world where the doctor does. */}
              {stats.misfiled.length > 0 && (
                <div className="mt-5 border-t border-slate-100 pt-4">
                  <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    <Trans>Frontières à revoir entre deux champs</Trans>
                  </div>
                  <ul className="mt-2 space-y-1">
                    {stats.misfiled.map((pair) => (
                      <li
                        key={`${pair.from_field}-${pair.to_field}`}
                        className="text-sm text-slate-700"
                      >
                        {fieldName(pair.from_field)}{" "}
                        <span className="text-slate-400">→</span>{" "}
                        {fieldName(pair.to_field)}{" "}
                        <span className="font-mono text-xs text-slate-500">
                          ×{pair.count}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {classification.unclassified_since_classes > 0 && (
                <p className="mt-4 text-xs text-slate-500">
                  <Trans>
                    {classification.unclassified_since_classes} corrections
                    enregistrées depuis sans classe — le formulaire ne bloque
                    jamais la sauvegarde pour l'obtenir.
                  </Trans>
                </p>
              )}
            </>
          )}

          {classification.unclassified_before_classes > 0 &&
            classification.classified > 0 && (
              <p className="mt-4 text-xs text-slate-500">
                <Trans>
                  {classification.unclassified_before_classes} corrections plus
                  anciennes restent hors de ce décompte : elles sont antérieures à
                  la question et ne sont pas rattrapables.
                </Trans>
              </p>
            )}
        </CardContent>
      </Card>

      {/* ═══ What clinicians wrote: the required prose and the optional notes ═══ */}
      {stats.justifications.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">
              <Trans>Les justifications écrites</Trans>
            </CardTitle>
            <p className="mt-1 text-sm text-slate-500">
              <Trans>
                Cinq classes sur huit exigent une phrase, et seulement quand ni
                le champ cible ni la valeur corrigée ne peut porter la raison.
                Ces phrases-là ne redisent pas la classe : elles sont la
                trouvaille. Les trois autres offrent une précision facultative,
                listée ici quand elle a été écrite : le terme absent d'une liste
                fermée, par exemple.
              </Trans>
            </p>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3">
              {stats.justifications.map((justification, index) => (
                <li
                  key={`${justification.field_key}-${justification.at}-${index}`}
                  className="border-l-2 border-primary/30 pl-3"
                >
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <span className="text-sm font-medium text-slate-900">
                      {justification.field_name || justification.field_key}
                    </span>
                    <Badge variant="outline" className="font-normal">
                      {t(errorClassOption(justification.error_class).label)}
                    </Badge>
                    <span className="text-3xs text-slate-400">
                      {formatMonth(justification.at)}
                    </span>
                  </div>
                  <p className="mt-0.5 text-sm text-slate-700">
                    {justification.note}
                  </p>
                </li>
              ))}
            </ul>
            {stats.justifications_total > stats.justifications.length && (
              <p className="mt-3 text-xs text-slate-500">
                <Trans>
                  Les {stats.justifications.length} plus récentes sur{" "}
                  {stats.justifications_total}.
                </Trans>
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* ═══ La forme, sur tout l'historique ═══ */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            <Trans>Comment la valeur a changé</Trans>
          </CardTitle>
          <p className="mt-1 text-sm text-slate-500">
            <Trans>
              Calculable sur la totalité du journal, classe ou pas. Ce n'est pas la
              raison de l'erreur, c'est sa forme — mais c'est ce que l'historique
              sait dire.
            </Trans>
          </p>
        </CardHeader>
        <CardContent className={cn("max-w-xl")}>
          {SHAPE_ORDER.map((shape) => (
            <RankedBar
              key={shape}
              label={shapeLabel[shape]}
              value={stats.shapes[shape]}
              total={totalCorrectionsWithShape}
            />
          ))}
          {totals.flagged > 0 && (
            <p className="mt-3 text-xs text-slate-500">
              <Trans>
                {totals.flagged} relectures signalées fausses ne figurent pas ici :
                la valeur n'a pas bougé, il n'y a donc aucune forme à décrire.
              </Trans>
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
